import React, { useState, useCallback } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
  Autocomplete,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from "@mui/material";
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from "@mui/icons-material";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import DateTimeRangePicker from '../../components/common/DateTimeRangePicker';
import AgentSelect from '../../components/common/AgentSelect';
import LeadSelect from '../../components/common/LeadSelect';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const CreateNewSiteVisit = ({ 
  open, 
  onClose, 
  onSave,
  agents = [],
  leads = [],
  projects = [],
  transportationOptions = [],
  visitTypeOptions = [],
  fetchConfigurationSelect,
  fetchUnitSelect
}) => {

  // State for configurations and units
  const [projectConfigurations, setProjectConfigurations] = useState([]);
  const [projectUnits, setProjectUnits] = useState([]);
  const [selectedConfiguration, setSelectedConfiguration] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  // Simple form state with only essential fields
  const [formData, setFormData] = useState({
    lead: '',
    agent: '',
    project: '',
    configurations: [],
    units: [],
    visit_type: 'physical',
    transport_mode: 'self',
    start_time: dayjs().tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss'),
    end_time: dayjs().add(1, 'hour').tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss'),
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle date/time changes from DateTimeRangePicker
  const handleDateTimeChange = useCallback(({ startDateTime, endDateTime }) => {
    setFormData(prev => ({
      ...prev,
      start_time: startDateTime,
      end_time: endDateTime
    }));
  }, []);

  // Handle form field changes
  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle lead selection
  const handleLeadChange = (selectedLead) => {
    setFormData(prev => ({ ...prev, lead: selectedLead || '' }));
    if (errors.lead) {
      setErrors(prev => ({ ...prev, lead: '' }));
    }
  };

  // Handle agent selection
  const handleAgentChange = (agentId, selectedAgent) => {
    setFormData(prev => ({ ...prev, agent: agentId || selectedAgent?.id || '' }));
    if (errors.agent) {
      setErrors(prev => ({ ...prev, agent: '' }));
    }
  };

  // Fetch configurations for a project
  const fetchConfigurationsForProject = async (projectId) => {
    try {
      const configurations = await fetchConfigurationSelect(projectId);
      setProjectConfigurations(configurations);
    } catch (error) {
      console.error('Error fetching configurations:', error);
      setProjectConfigurations([]);
    }
  };

  // Fetch units for a configuration
  const fetchUnitsForConfiguration = async (projectId, configId) => {
    try {
      const units = await fetchUnitSelect(projectId, configId);
      setProjectUnits(units);
    } catch (error) {
      console.error('Error fetching units:', error);
      setProjectUnits([]);
    }
  };

  // Handle project selection
  const handleProjectChange = (selectedProject) => {
    setFormData(prev => ({ ...prev, project: selectedProject?.id || '' }));
    
    // Clear configurations and units when project changes
    setProjectConfigurations([]);
    setProjectUnits([]);
    setSelectedConfiguration(null);
    setSelectedUnit(null);
    setFormData(prev => ({ 
      ...prev, 
      project: selectedProject?.id || '',
      configurations: [],
      units: []
    }));
    
    if (errors.project) {
      setErrors(prev => ({ ...prev, project: '' }));
    }
    
    // Fetch configurations for this project
    if (selectedProject?.id) {
      fetchConfigurationsForProject(selectedProject.id);
    }
  };

  // Handle configuration selection
  const handleConfigurationChange = (selectedConfig) => {
    setSelectedConfiguration(selectedConfig);
    setSelectedUnit(null);
    setProjectUnits([]);
    
    if (selectedConfig?.id && formData.project) {
      fetchUnitsForConfiguration(formData.project, selectedConfig.id);
    }
  };

  // Handle unit selection
  const handleUnitChange = (selectedUnit) => {
    setSelectedUnit(selectedUnit);
  };

  // Add configuration and unit to the list
  const handleAddConfigurationUnit = () => {
    if (!selectedConfiguration) {
      setErrors(prev => ({ ...prev, configuration: 'Please select a configuration' }));
      return;
    }

    const configUnitPair = {
      configuration: selectedConfiguration,
      unit: selectedUnit
    };

    setFormData(prev => ({
      ...prev,
      configurations: [...prev.configurations, configUnitPair]
    }));

    // Clear selections
    setSelectedConfiguration(null);
    setSelectedUnit(null);
    setProjectUnits([]);
    
    // Clear errors
    if (errors.configuration) {
      setErrors(prev => ({ ...prev, configuration: '' }));
    }
  };

  // Remove configuration and unit from the list
  const handleRemoveConfigurationUnit = (index) => {
    setFormData(prev => ({
      ...prev,
      configurations: prev.configurations.filter((_, i) => i !== index)
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.lead || formData.lead === '') newErrors.lead = 'Please select a lead';
    if (!formData.agent || formData.agent === '') newErrors.agent = 'Please select an agent';
    if (!formData.visit_type) newErrors.visit_type = 'Please select a visit type';
    if (!formData.transport_mode) newErrors.transport_mode = 'Please select a transport mode';
    // Project is now optional - can be mapped later when editing
    // if (!formData.project) newErrors.project = 'Please select a project';
    if (!formData.start_time || formData.start_time === '') newErrors.start_time = 'Please select a date and time';
    if (!formData.end_time || formData.end_time === '') newErrors.end_time = 'Please select an end time';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      const payload = {
        lead: formData.lead && formData.lead !== '' ? formData.lead : null,
        agent: formData.agent && formData.agent !== '' ? formData.agent : null,
        project: formData.project && formData.project !== '' ? formData.project : null,
        visit_units: formData.configurations.length > 0 ? formData.configurations.map(item => ({
          configuration: typeof item.configuration === 'string' ? item.configuration : item.configuration.name,
          unit: item.unit,
          notes: null
        })) : [],
        start_time: formData.start_time ? formData.start_time : null,
        end_time: formData.end_time ? formData.end_time : null,
        agent_notes: formData.notes.trim() || undefined,
        visit_type: formData.visit_type,
        transportation_type: formData.transport_mode
      };


      await onSave(payload);
      
      // Reset form and close modal only on success
      setFormData({
        lead: '',
        agent: '',
        project: '',
        configurations: [],
        units: [],
        visit_type: 'physical',
        transport_mode: 'self',
        start_time: dayjs().tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss'),
        end_time: dayjs().add(1, 'hour').tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss'),
        notes: ''
      });
      setProjectConfigurations([]);
      setProjectUnits([]);
      setSelectedConfiguration(null);
      setSelectedUnit(null);
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error creating site visit:', error);
      
      // Handle API errors gracefully
      if (error.response?.data) {
        const apiErrors = error.response.data;
        
        // Convert API errors to form errors
        const newErrors = {};
        
        if (apiErrors.lead) {
          newErrors.lead = Array.isArray(apiErrors.lead) ? apiErrors.lead[0] : apiErrors.lead;
        }
        if (apiErrors.agent) {
          newErrors.agent = Array.isArray(apiErrors.agent) ? apiErrors.agent[0] : apiErrors.agent;
        }
        if (apiErrors.project) {
          newErrors.project = Array.isArray(apiErrors.project) ? apiErrors.project[0] : apiErrors.project;
        }
        if (apiErrors.visit_type) {
          newErrors.visit_type = Array.isArray(apiErrors.visit_type) ? apiErrors.visit_type[0] : apiErrors.visit_type;
        }
        if (apiErrors.transportation_type) {
          newErrors.transport_mode = Array.isArray(apiErrors.transportation_type) ? apiErrors.transportation_type[0] : apiErrors.transportation_type;
        }
        if (apiErrors.start_time) {
          newErrors.start_time = Array.isArray(apiErrors.start_time) ? apiErrors.start_time[0] : apiErrors.start_time;
        }
        if (apiErrors.end_time) {
          newErrors.end_time = Array.isArray(apiErrors.end_time) ? apiErrors.end_time[0] : apiErrors.end_time;
        }
        if (apiErrors.agent_notes) {
          newErrors.notes = Array.isArray(apiErrors.agent_notes) ? apiErrors.agent_notes[0] : apiErrors.agent_notes;
        }
        if (apiErrors.configurations) {
          newErrors.configuration = Array.isArray(apiErrors.configurations) ? apiErrors.configurations[0] : apiErrors.configurations;
        }
        
        // Handle non-field errors
        if (apiErrors.non_field_errors) {
          newErrors.general = Array.isArray(apiErrors.non_field_errors) ? apiErrors.non_field_errors[0] : apiErrors.non_field_errors;
        }
        
        setErrors(newErrors);
      } else if (error.message) {
        // Handle JavaScript errors
        setErrors({ general: `Error: ${error.message}` });
      } else if (error.isAuthError) {
        // Handle authentication errors
        setErrors({ general: 'Authentication error. Please log in again.' });
      } else {
        // Handle network or other errors
        setErrors({ general: 'Failed to create site visit. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when modal closes
  const handleClose = () => {
    // Only reset form if there are no errors (user is closing without submitting)
    if (Object.keys(errors).length === 0) {
      setFormData({
        lead: '',
        agent: '',
        project: '',
        configurations: [],
        units: [],
        visit_type: 'physical',
        transport_mode: 'self',
        start_time: dayjs().tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss'),
        end_time: dayjs().add(1, 'hour').tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss'),
        notes: ''
      });
      setProjectConfigurations([]);
      setProjectUnits([]);
      setSelectedConfiguration(null);
      setSelectedUnit(null);
    }
    setErrors({});
    onClose();
  };

  // Get selected objects for display
  const selectedLead = leads.find(l => l.value === formData.lead);
  const selectedProject = projects.find(p => p.id === formData.project);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Schedule New Site Visit
            </Typography>
          </Box>
          <Button
            onClick={handleClose}
            sx={{ 
              color: 'text.secondary',
              minWidth: 'auto',
              p: 1
            }}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {/* General Error Alert */}
          {errors.general && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                '& .MuiAlert-message': {
                  fontSize: '0.875rem',
                  lineHeight: 1.4
                }
              }}
              onClose={() => setErrors(prev => ({ ...prev, general: '' }))}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Unable to Create Site Visit
                </Typography>
                <Typography variant="body2" sx={{ color: 'error.dark' }}>
                  {errors.general}
                </Typography>
              </Box>
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            {/* Step 1: Who */}
            <Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: 'text.primary',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <PersonIcon fontSize="small" color="primary" />
                Who is this visit for?
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                 {/* Lead Selection */}
                 <Box>
                   <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                     Lead *
                   </Typography>
                   <LeadSelect
                     value={formData.lead}
                     onChange={handleLeadChange}
                     options={leads}
                     error={errors.lead}
                     helperText={errors.lead || "Select a lead for this visit"}
                     required
                     label="Select Lead"
                   />
                 </Box>

                {/* Agent Selection */}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                    Agent *
                  </Typography>
                  <AgentSelect
                    agents={agents}
                    value={formData.agent}
                    onChange={handleAgentChange}
                    variant="assign"
                    multiple={false}
                    error={errors.agent}
                    helperText={errors.agent || "Select an agent to conduct the visit"}
                    required
                  />
                </Box>

                {/* Visit Type and Transport Mode */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {/* Visit Type */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                      Visit Type *
                    </Typography>
                    <FormControl fullWidth size="small" error={!!errors.visit_type}>
                      <Select
                        value={formData.visit_type}
                        onChange={handleChange('visit_type')}
                        displayEmpty
                        sx={{ borderRadius: 2 }}
                      >
                        {visitTypeOptions.map(vtype => (
                          <MenuItem value={vtype.value}>{vtype.label}</MenuItem>
                        ))}
                      </Select>
                      {errors.visit_type && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                          {errors.visit_type}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>

                  {/* Transport Mode */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                      Transport Mode *
                    </Typography>
                    <FormControl fullWidth size="small" error={!!errors.transport_mode}>
                      <Select
                        value={formData.transport_mode}
                        onChange={handleChange('transport_mode')}
                        displayEmpty
                        sx={{ borderRadius: 2 }}
                      > 
                        {transportationOptions.map(transp => (
                          <MenuItem value={transp.value}>{transp.label}</MenuItem>
                        ))}
                      </Select>
                      {errors.transport_mode && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                          {errors.transport_mode}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Step 2: Where */}
             <Box>
               <Typography variant="h6" sx={{ 
                 fontWeight: 600, 
                 color: 'text.primary',
                 mb: 2,
                 display: 'flex',
                 alignItems: 'center',
                 gap: 1
               }}>
                 <BusinessIcon fontSize="small" color="primary" />
                 Which property?
               </Typography>
               
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                 {/* Project Selection */}
                 <Box>
                   <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                     Project (Optional)
                   </Typography>
                   <Autocomplete
                     options={projects}
                     getOptionLabel={(option) => option?.name || ''}
                     value={projects.find(p => p.id === formData.project) || null}
                     onChange={(_, newValue) => handleProjectChange(newValue)}
                     isOptionEqualToValue={(option, value) => option?.id === value?.id}
                     renderInput={(params) => (
                       <TextField
                         {...params}
                         label="Select Project (Optional)"
                         variant="outlined"
                         size="small"
                         error={!!errors.project}
                         helperText={errors.project || "Project can be mapped later when editing"}
                         fullWidth
                       />
                     )}
                     disabled={false}
                     clearOnEscape
                   />
                 </Box>

                 {/* Configuration and Unit Selection */}
                 {formData.project && (
                   <Box>
                     <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                       Select Configurations & Units
                     </Typography>
                     
                     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                       {/* Configuration and Unit Selection Row */}
                       <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                         <Autocomplete
                           options={projectConfigurations}
                           getOptionLabel={(option) => option?.name || ''}
                           value={selectedConfiguration}
                           onChange={(_, newValue) => handleConfigurationChange(newValue)}
                           isOptionEqualToValue={(option, value) => option?.id === value?.id}
                           renderInput={(params) => (
                             <TextField
                               {...params}
                               label="Configuration"
                               variant="outlined"
                               size="small"
                               error={!!errors.configuration}
                               helperText={errors.configuration}
                               sx={{ minWidth: 150 }}
                             />
                           )}
                           disabled={projectConfigurations.length === 0}
                           clearOnEscape
                         />
                         
                         <Autocomplete
                           options={projectUnits}
                           getOptionLabel={(option) => option?.name || option?.unit_number || ''}
                           value={selectedUnit}
                           onChange={(_, newValue) => handleUnitChange(newValue)}
                           isOptionEqualToValue={(option, value) => option?.id === value?.id}
                           renderInput={(params) => (
                             <TextField
                               {...params}
                               label="Unit (Optional)"
                               variant="outlined"
                               size="small"
                               sx={{ minWidth: 150 }}
                             />
                           )}
                           disabled={projectUnits.length === 0}
                           clearOnEscape
                         />
                         
                         <Button
                           variant="contained"
                           color="primary"
                           onClick={handleAddConfigurationUnit}
                           disabled={!selectedConfiguration}
                           sx={{ 
                             textTransform: 'none',
                             borderRadius: 2,
                             px: 2,
                             minWidth: 80
                           }}
                         >
                           Add
                         </Button>
                       </Box>

                       {/* Selected Configurations and Units List */}
                       {formData.configurations.length > 0 && (
                         <Box sx={{ 
                           p: 2, 
                           borderRadius: 2, 
                           bgcolor: 'grey.50',
                           border: '1px solid',
                           borderColor: 'grey.200'
                         }}>
                           <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                             Selected Configurations & Units:
                           </Typography>
                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                             {formData.configurations.map((item, index) => (
                               <Box key={index} sx={{ 
                                 display: 'flex', 
                                 justifyContent: 'space-between', 
                                 alignItems: 'center',
                                 p: 1,
                                 borderRadius: 1,
                                 bgcolor: 'white'
                               }}>
                                 <Typography variant="body2">
                                   <strong>{item.configuration.name}</strong>
                                   {item.unit && ` - ${item.unit.name}`}
                                 </Typography>
                                 <Button
                                   size="small"
                                   color="error"
                                   onClick={() => handleRemoveConfigurationUnit(index)}
                                   sx={{ textTransform: 'none', minWidth: 'auto', p: 0.5 }}
                                 >
                                   Remove
                                 </Button>
                               </Box>
                             ))}
                           </Box>
                         </Box>
                       )}
                     </Box>
                   </Box>
                 )}
               </Box>
             </Box>

            {/* Step 3: When */}
            <Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: 'text.primary',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <ScheduleIcon fontSize="small" color="primary" />
                When is the visit?
              </Typography>
              
              <DateTimeRangePicker
                initialStartDateTime={formData.start_time}
                initialEndDateTime={formData.end_time}
                onChange={handleDateTimeChange}
              />
              
              {(errors.start_time || errors.end_time) && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.start_time || errors.end_time}
                </Typography>
              )}
            </Box>

            {/* Step 4: Notes (Optional) */}
            <Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: 'text.primary',
                mb: 2
              }}>
                Any special notes? (Optional)
              </Typography>
              
              <TextField
                multiline
                rows={3}
                fullWidth
                placeholder="Add any special instructions or notes for this visit..."
                value={formData.notes}
                onChange={handleChange('notes')}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: '0.875rem'
                  }
                }}
              />
            </Box>
          </Box>
        </LocalizationProvider>
      </DialogContent>

      {/* Footer with Actions */}
      <DialogActions sx={{ 
        p: 3, 
        pt: 2, 
        borderTop: '1px solid', 
        borderColor: 'divider',
        flexDirection: 'column',
        alignItems: 'stretch'
      }}>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button 
            onClick={handleClose} 
            variant="outlined" 
            color="inherit"
            disabled={isSubmitting}
            sx={{ 
              textTransform: 'none',
              borderRadius: 2,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{ 
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              minWidth: 140
            }}
          >
            {isSubmitting ? 'Scheduling...' : 'Schedule Visit'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CreateNewSiteVisit;
